import { Box, Chip, Table, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

export const TableWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '0 20px 20px',
  minHeight: '78vh',
}));

export const QuickFiltersBox = styled(Box)(() => ({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  alignItems: 'center',
  paddingTop: '4px',
}));

export const FilterChip = styled(Chip)(({ theme }) => ({
  borderRadius: '16px',
  fontWeight: 500,
  cursor: 'pointer',
  borderColor: theme.palette.divider,
  '&.MuiChip-filledPrimary': {
    color: '#FAFCFF',
  },
}));

export const TableScroll = styled(Box)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#FFFFFF',
  '&::-webkit-scrollbar': {
    height: '12px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '8px',
  },
}));

export const StyledTable = styled(Table)(() => ({
  '& thead th': {
    fontWeight: 600,
    fontSize: '13px',
    letterSpacing: '0.01em',
    backgroundColor: '#F2EFFF',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  '& tbody td': {
    fontSize: '13px',
    paddingTop: '4px',
    paddingBottom: '4px',
    whiteSpace: 'nowrap',
  },
}));

export const HeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'sortable',
})<{ sortable?: boolean }>(({ sortable, theme }) => ({
  cursor: sortable ? 'pointer' : 'default',
  userSelect: 'none',
  '&:hover': sortable ? { color: theme.palette.primary.main } : undefined,
  '& .sort-icon': {
    fontSize: '16px',
    verticalAlign: 'middle',
    marginLeft: '4px',
    opacity: 0.6,
  },
  '&.active .sort-icon': {
    opacity: 1,
    color: theme.palette.primary.main,
  },
}));

export const BodyRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  height: '36px',
  '&:hover': {
    backgroundColor: 'rgba(123, 63, 242, 0.06)',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#FAFBFD',
    '&:hover': {
      backgroundColor: 'rgba(123, 63, 242, 0.06)',
    },
  },
  '& a': {
    color: theme.palette.text.primary,
    textDecoration: 'none',
  },
}));

export const SlaBadge = styled('span')<{ tone: 'overdue' | 'near' | 'ok' }>(
  ({ tone }) => {
    const map = {
      overdue: { bg: '#FDECEC', fg: '#B71C1C' },
      near: { bg: '#FFF4E5', fg: '#9A4F00' },
      ok: { bg: '#E6F4EA', fg: '#1E6630' },
    } as const;
    const palette = map[tone];
    return {
      backgroundColor: palette.bg,
      color: palette.fg,
      borderRadius: '999px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      display: 'inline-block',
    };
  }
);

export const StatusBadge = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'lowercase',
  backgroundColor: theme.palette.primary.main,
  color: '#FAFCFF',
  whiteSpace: 'nowrap',
}));

export const Toolbar = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  padding: '6px 0',
}));

export const PaginationControls = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontSize: '13px',
  color: '#555',
}));

export const EmptyStateBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  gap: '8px',
  color: '#777',
  '& .material-symbols-outlined': { fontSize: '36px' },
}));
